import { useState } from "react";
import { ArrowLeft, ArrowRight, BookOpen, MessageCircle, Sprout, Users } from "lucide-react";
import { PhoneFrame } from "@/components/storyboard/PhoneStoryboard";
import type { StoryboardScreenId } from "@/components/storyboard/storyboardData";

type JourneyStep = {
  id: StoryboardScreenId;
  label: string;
  title: string;
  caption: string;
  proof: string;
  icon: typeof BookOpen;
};

const JOURNEY: JourneyStep[] = [
  {
    id: "onboarding",
    label: "Sprints",
    title: "Choose a sprint for one Fruit.",
    caption: "Daily, weekly, and monthly paths bring verses, resources, check-ins, and community support around the same Fruit.",
    proof: "Daily / weekly / monthly",
    icon: Sprout,
  },
  {
    id: "verse",
    label: "Word",
    title: "Scripture arrives like a prepared gift.",
    caption: "Verse, theme, application, and prayer unfold in a clear devotional sequence.",
    proof: "Daily verse reveal",
    icon: BookOpen,
  },
  {
    id: "chat",
    label: "Conversation",
    title: "The chat stays anchored in the passage.",
    caption: "The companion explains why a verse was chosen and keeps the next step grounded.",
    proof: "Scripture-led AI",
    icon: MessageCircle,
  },
  {
    id: "vine",
    label: "Growth",
    title: "Progress looks like formation, not pressure.",
    caption: "Faith Vine makes rhythm visible with grace, reflection, and a simple return path.",
    proof: "Grace-based streak",
    icon: Sprout,
  },
  {
    id: "groups",
    label: "Together",
    title: "Prayer circles feel private and encouraging.",
    caption: "Group momentum is shown without turning faith into a public scoreboard.",
    proof: "Shared prayer rhythm",
    icon: Users,
  },
];

export function UXGallerySection() {
  const [active, setActive] = useState(0);
  const activeStep = JOURNEY[active];
  const previousStep = JOURNEY[(active + JOURNEY.length - 1) % JOURNEY.length];
  const nextStep = JOURNEY[(active + 1) % JOURNEY.length];
  const ActiveIcon = activeStep.icon;

  const goToPrevious = () => setActive((current) => (current === 0 ? JOURNEY.length - 1 : current - 1));
  const goToNext = () => setActive((current) => (current + 1) % JOURNEY.length);

  return (
    <section
      id="experience"
      data-active-step={activeStep.id}
      className="relative overflow-hidden bg-[#050504] py-24 text-white md:py-32"
    >
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,#050504_0%,#11100d_46%,#050504_100%)]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#d9a441]/45 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />

      <div className="relative mx-auto max-w-7xl px-6 md:px-12 lg:px-20">
        <div className="grid gap-12 lg:grid-cols-[0.78fr_1.22fr] lg:items-center">
          <div>
            <div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.22em] text-[#d9a441]">
              <span className="h-px w-10 bg-[#d9a441]" />
              iPhone experience
            </div>
            <h2 className="mt-5 max-w-xl text-5xl font-semibold leading-[0.98] tracking-tighter text-white md:text-6xl">
              One walk, start to finish.
            </h2>
            <p className="mt-6 max-w-lg text-base leading-7 text-white/62">
              A premium mobile journey for scripture, reflection, conversation, and sprint-based Fruit growth.
            </p>

            <div className="mt-9 space-y-2" role="tablist" aria-label="Experience screens">
              {JOURNEY.map((step, index) => {
                const isActive = index === active;
                const StepIcon = step.icon;

                return (
                  <button
                    key={step.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    data-testid={`experience-step-${step.id}`}
                    onClick={() => setActive(index)}
                    onFocus={() => setActive(index)}
                    onMouseEnter={() => setActive(index)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setActive(index);
                      }
                    }}
                    className={[
                      "group grid w-full grid-cols-[auto_1fr_auto] items-center gap-4 rounded-[26px] border px-4 py-4 text-left transition",
                      isActive
                        ? "border-[#d9a441]/45 bg-white/[0.08] text-white shadow-[0_24px_70px_-52px_rgba(217,164,65,0.85)]"
                        : "border-white/[0.08] bg-white/[0.025] text-white/58 hover:border-white/16 hover:bg-white/[0.05] hover:text-white",
                    ].join(" ")}
                  >
                    <span
                      className={[
                        "grid h-11 w-11 place-items-center rounded-2xl border text-sm font-semibold tabular-nums",
                        isActive ? "border-[#d9a441]/40 bg-[#d9a441] text-[#17110a]" : "border-white/10 text-white/45",
                      ].join(" ")}
                    >
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <span className="min-w-0">
                      <span className="flex items-center gap-2 text-sm font-semibold">
                        <StepIcon size={15} />
                        {step.label}
                      </span>
                      <span className="mt-1 block truncate text-xs text-white/45">{step.proof}</span>
                    </span>
                    <span className={isActive ? "h-8 w-1 rounded-full bg-[#d9a441]" : "h-8 w-1 rounded-full bg-white/10"} />
                  </button>
                );
              })}
            </div>
          </div>

          <div className="relative min-h-[610px] overflow-hidden rounded-[42px] border border-white/[0.08] bg-[linear-gradient(145deg,rgba(255,255,255,0.08),rgba(255,255,255,0.02)_34%,rgba(217,164,65,0.07))] px-5 py-8 shadow-[0_50px_140px_-70px_rgba(0,0,0,0.95)] md:min-h-[690px] md:px-10">
            <div className="pointer-events-none absolute inset-x-12 top-10 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />
            <div className="pointer-events-none absolute inset-x-10 bottom-12 h-24 rounded-[100%] bg-black/55 blur-2xl" />

            <div className="absolute left-5 top-28 hidden rotate-[-8deg] opacity-40 blur-[0.2px] xl:block">
              <PhoneFrame screenId={previousStep.id} chrome="dark" scale={0.43} />
            </div>
            <div className="absolute right-5 top-32 hidden rotate-[7deg] opacity-40 blur-[0.2px] xl:block">
              <PhoneFrame screenId={nextStep.id} chrome="dark" scale={0.43} />
            </div>

            <div className="relative z-10 mx-auto w-full max-w-[288px] pt-2 md:max-w-[318px]">
              <PhoneFrame key={activeStep.id} screenId={activeStep.id} chrome="dark" scale={0.72} />
            </div>

            <div className="relative z-20 mx-auto mt-6 max-w-xl rounded-[30px] border border-white/[0.1] bg-[#0f0d0a]/78 p-5 backdrop-blur-xl">
              <div className="flex items-start justify-between gap-5">
                <div>
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#d9a441]">
                    <ActiveIcon size={14} />
                    {activeStep.label}
                  </div>
                  <h3 className="mt-3 text-2xl font-semibold leading-tight tracking-tight text-white">
                    {activeStep.title}
                  </h3>
                  <p className="mt-3 max-w-md text-sm leading-6 text-white/58">{activeStep.caption}</p>
                </div>
                <div className="hidden rounded-2xl border border-white/10 bg-white/[0.06] px-3 py-2 text-right sm:block">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-white/38">Step</p>
                  <p className="mt-1 text-xl font-semibold tabular-nums text-white">
                    {String(active + 1).padStart(2, "0")}
                    <span className="text-white/28">/{String(JOURNEY.length).padStart(2, "0")}</span>
                  </p>
                </div>
              </div>

              <div className="mt-5 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={goToPrevious}
                  className="grid h-11 w-11 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-white/70 transition hover:bg-white/[0.1] hover:text-white"
                  aria-label="Show previous screen"
                >
                  <ArrowLeft size={18} />
                </button>
                <div className="flex flex-1 gap-1.5">
                  {JOURNEY.map((step, index) => (
                    <button
                      key={step.id}
                      type="button"
                      onClick={() => setActive(index)}
                      aria-label={`Show ${step.label} screen`}
                      className={[
                        "h-1.5 flex-1 rounded-full transition",
                        index === active ? "bg-[#d9a441]" : "bg-white/14 hover:bg-white/28",
                      ].join(" ")}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={goToNext}
                  className="grid h-11 w-11 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-white/70 transition hover:bg-white/[0.1] hover:text-white"
                  aria-label="Show next screen"
                >
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
